import { Avatar, Box, Chip, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import PhoneNumberDisplay from '../common/components/PhoneNumberDisplay';
import { useGetUserContactInformationQuery } from '../queries/__generated__/getUserContactInformation.generated';
import PhotosUtils from '../common/utils/photos.utils';
import { currentContextAtom } from '../state';
import { UserDisplay } from './CompanyUsers';
import { useCompanyTeamRoleWording } from './team/useCompanyTeamRoleWording';

interface UserCardProps {
  user: UserDisplay;
}
export default function UserCard({ user }: UserCardProps): React.JSX.Element {
  const { t } = useTranslation(['company', 'common']);
  const { companyBrand } = useAtomValue(currentContextAtom);
  const { data: userContactInfoData } = useGetUserContactInformationQuery({
    variables: {
      userId: user.id,
    },
  });
  const resolvedWording = useCompanyTeamRoleWording(companyBrand || undefined);

  const {
    mobilePhoneComponent,
    landlinePhoneComponent,
    emailComponent,
  } = useMemo(() => {
    if (!userContactInfoData?.getUserContactInformation) {
      return {
        mobilePhoneComponent: undefined,
        landlinePhoneComponent: undefined,
        emailComponent: undefined,
      };
    }

    const { email, mobilePhoneNumber, landlinePhoneNumber } = userContactInfoData.getUserContactInformation;

    return {
      mobilePhoneComponent: (
        mobilePhoneNumber ? <PhoneNumberDisplay typographyVariant="bodySmall" phoneNumber={mobilePhoneNumber} shouldDisplayFlag={false} /> : '-'
      ),
      landlinePhoneComponent: (
        landlinePhoneNumber ? <PhoneNumberDisplay typographyVariant="bodySmall" phoneNumber={landlinePhoneNumber} shouldDisplayFlag={false} /> : '-'
      ),
      emailComponent: (
        email ? <Typography variant="bodySmall" color="secondary" sx={{ wordBreak: 'break-all' }}>{email}</Typography> : undefined
      ),
    };
  }, [userContactInfoData]);

  return (
    <Box
      sx={{
        p: 1.5,
        gap: 1.5,
        display: 'flex',
        flexDirection: {
          xs: 'column',
          md: 'row',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          flex: 1,
        }}
      >
        <Avatar variant="square" src={PhotosUtils.getUserAvatarPicture(user.avatars)} size={64} />
        <Box>
          <Typography variant="bodyMedium">{user.firstName}&nbsp;{user.lastName}</Typography>
          {emailComponent}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 2,
          flex: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <Typography variant="bodySmall" color="secondary" sx={{ mb: 0.5 }}>{t('company:role')}</Typography>
          {user.role_name ? (<Chip variant="tag" label={t(resolvedWording[user.role_name])} />) : '-'}
        </Box>
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <Typography variant="bodySmall" color="secondary" sx={{ mb: 0.5 }}>{t('common:mobile_phone')}</Typography>
          {mobilePhoneComponent}
        </Box>
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <Typography variant="bodySmall" color="secondary" sx={{ mb: 0.5 }}>{t('common:landline_phone')}</Typography>
          {landlinePhoneComponent}
        </Box>
      </Box>
    </Box>
  );
}
